function def(event, entity, observed = false) {
    return { event, entity, observed };
}
/**
 * Keyed by our own stable identifier so a service never types a monday string inline.
 * The value is what actually reaches the `activity_log` row.
 */
export const ACTIVITY_EVENTS = {
    /* ── items ("pulses") — 11 of these are observed ──────────────────────── */
    createItem: def('create_pulse', 'pulse', true),
    renameItem: def('update_name', 'pulse', true),
    moveItemGroup: def('move_pulse_from_group', 'pulse', true),
    moveItemBoard: def('move_pulse_from_board', 'pulse', true),
    /**
     * monday writes the `from` row on the **source** board and the `into` row on the
     * **destination** board for one move; both share the `action_record_uuid` `[av]`.
     */
    moveItemIntoGroup: def('move_pulse_into_group', 'pulse', true),
    moveItemIntoBoard: def('move_pulse_into_board', 'pulse', true),
    archiveItem: def('archive_pulse', 'pulse', true),
    deleteItem: def('delete_pulse', 'pulse', true),
    unarchiveItem: def('unarchive_pulse', 'pulse'),
    restoreItem: def('restore_pulse', 'pulse'),
    duplicateItem: def('duplicate_pulse', 'pulse'),
    subscribeItem: def('add_owner', 'pulse', true),
    unsubscribeItem: def('unsubscribe', 'pulse', true),
    batchDeleteItems: def('batch_delete_pulses', 'pulse', true),
    batchArchiveItems: def('batch_archive_pulses', 'pulse'),
    batchMoveItems: def('batch_move_pulses', 'pulse'),
    batchDuplicateItems: def('batch_duplicate_pulses', 'pulse'),
    /* ── cells and columns ────────────────────────────────────────────────── */
    updateColumnValue: def('update_column_value', 'pulse', true),
    batchUpdateColumnValue: def('batch_change_pulses_column_value', 'pulse', true),
    createColumn: def('create_column', 'board', true),
    renameColumn: def('update_column_name', 'board', true),
    changeColumnSettings: def('change_column_settings', 'board', true),
    duplicateColumn: def('duplicate_column', 'board'),
    deleteColumn: def('delete_column', 'board', true),
    archiveColumn: def('archive_column', 'board'),
    restoreColumn: def('restore_column', 'board'),
    moveColumn: def('move_column', 'board'),
    changeColumnWidth: def('change_column_width', 'board'),
    restrictColumn: def('change_column_permissions', 'board'),
    /* ── groups — create/rename/delete measured `[av]`; the rest ASSUMED (Q149) ── */
    createGroup: def('create_group', 'board', true),
    /** Measured as `update_group_name`; `update_group_title` was an ASSUMED guess (q252). */
    renameGroup: def('update_group_name', 'board', true),
    recolourGroup: def('update_group_color', 'board'),
    moveGroup: def('move_group', 'board'),
    duplicateGroup: def('duplicate_group', 'board'),
    archiveGroup: def('archive_group', 'board'),
    unarchiveGroup: def('unarchive_group', 'board'),
    deleteGroup: def('delete_group', 'board', true),
    restoreGroup: def('restore_group', 'board'),
    /* ── boards — only the three view/subscription rows are observed ──────── */
    createBoard: def('create_board', 'board'),
    renameBoard: def('update_board_name', 'board'),
    describeBoard: def('update_board_description', 'board'),
    boardCommunication: def('update_board_communication', 'board'),
    /** Measured: monday calls the item-terminology change `update_board_nickname` `[av]`. */
    boardTerminology: def('update_board_nickname', 'board', true),
    boardPermissions: def('update_board_permissions', 'board', true),
    /** Measured: moving a board between workspaces `[av]`. */
    moveBoardWorkspace: def('board_workspace_id_changed', 'board', true),
    changeBoardKind: def('change_board_kind', 'board'),
    archiveBoard: def('archive_board', 'board'),
    restoreBoard: def('restore_board', 'board'),
    deleteBoard: def('delete_board', 'board'),
    restoreBoardFromTrash: def('restore_board_from_trash', 'board'),
    duplicateBoard: def('duplicate_board', 'board'),
    moveBoard: def('move_board', 'board'),
    /** `item_type: "Board"`, `pulse_id: null` — the observed board-subscription pair `[ba§2]`. */
    subscribeBoard: def('subscribe', 'pulse', true),
    unsubscribeBoard: def('unsubscribe', 'pulse', true),
    boardSubscriberRole: def('update_board_subscriber_role', 'board'),
    /* ── views — only `board_view_added` is observed (and monday mislabels it) ── */
    addView: def('board_view_added', 'pulse', true),
    updateView: def('board_view_updated', 'board'),
    duplicateView: def('board_view_duplicated', 'board'),
    deleteView: def('board_view_deleted', 'board'),
    restoreView: def('board_view_restored', 'board'),
    moveView: def('move_board_view', 'board'),
    setDefaultView: def('board_view_set_default', 'board'),
    /* ── workspaces and folders — `board` because no other entity value exists ── */
    createWorkspace: def('create_workspace', 'board'),
    renameWorkspace: def('update_workspace_name', 'board'),
    describeWorkspace: def('update_workspace_description', 'board'),
    changeWorkspaceKind: def('change_workspace_kind', 'board'),
    deleteWorkspace: def('delete_workspace', 'board'),
    restoreWorkspace: def('restore_workspace', 'board'),
    createFolder: def('create_folder', 'board'),
    renameFolder: def('update_folder_name', 'board'),
    moveFolder: def('move_folder', 'board'),
    deleteFolder: def('delete_folder', 'board'),
};
/**
 * Every monday event string this product can write, **deduplicated**: two keys deliberately
 * share `unsubscribe` (monday uses the one name for the board and the item case, telling them
 * apart by `data.item_type` `[av]`).
 */
export const ACTIVITY_EVENT_NAMES = [
    ...new Set(Object.values(ACTIVITY_EVENTS).map((definition) => definition.event)),
];
/** The 25 names actually seen in monday's own log `[av]`. */
export const OBSERVED_ACTIVITY_EVENT_NAMES = [
    ...new Set(Object.values(ACTIVITY_EVENTS)
        .filter((definition) => definition.observed)
        .map((definition) => definition.event)),
];
export function activityCommonFields(actionRecordUuid, options = {}) {
    return {
        action_record_uuid: actionRecordUuid,
        is_batch_action: options.batch ?? false,
        is_undo_action: options.undo ?? false,
    };
}
/**
 * monday renders `activity_logs.created_at` as a **17-digit** integer of 100-ns ticks since the
 * Unix epoch (`17889558789972794` → `2026-09-09T12:11:18.997Z`) `[ba§2]`, `[M9§7.2.4]`.
 */
export function toMondayActivityTimestamp(date) {
    return (BigInt(date.getTime()) * 10000n).toString();
}
export function fromMondayActivityTimestamp(ticks) {
    return new Date(Number(BigInt(ticks) / 10000n));
}
//# sourceMappingURL=events.js.map